import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const AdminBlogList = () => {
  const MASTER_KEY = "axp1231";
  const [inputKey, setInputKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Authenticate Admin node access layer
  const handleAuthSubmit = (e) => {
    e.preventDefault();
    if (inputKey === MASTER_KEY) setIsAuthenticated(true);
  };

  // Pull database records stream on mount when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchAdminBlogs = async () => {
      try {
        const response = await fetch('/api/blogs/list');
        const result = await response.json();

        if (response.ok && result.success) {
          setBlogs(result.data);
        } else {
          setError(result.message || 'Failed to sync database logs.');
        }
      } catch (err) {
        console.error('API Connection Failure:', err);
        setError('Could not connect to the backend core engine data streams.');
      } finally {
        setLoading(false);
      }
    };

    fetchAdminBlogs();
  }, [isAuthenticated]);

  const getTagColorClass = (tag) => {
    const cleanTag = tag?.toLowerCase() || '';
    if (cleanTag.includes('grow') || cleanTag.includes('strat')) return 'text-indigo-600 bg-indigo-50 border-indigo-100';
    if (cleanTag.includes('auto') || cleanTag.includes('bot')) return 'text-teal-600 bg-teal-50 border-teal-100';
    if (cleanTag.includes('eng') || cleanTag.includes('code')) return 'text-purple-600 bg-purple-50 border-purple-100';
    return 'text-sky-600 bg-sky-50 border-sky-100';
  };

  // Security Gate UI Layout - Premium Light Modern Entry Frame
  if (!isAuthenticated) {
    return (
      <div className="bg-slate-50 text-slate-800 min-h-screen flex items-center justify-center font-sans antialiased relative px-4">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-screen-2xl h-[400px] bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.05),transparent_50%)] pointer-events-none z-0" />
        
        <form onSubmit={handleAuthSubmit} className="bg-white p-10 rounded-3xl w-full max-w-md border border-slate-200/80 shadow-xl shadow-slate-200/40 text-center relative z-10">
          <div className="text-3xl mb-3">🔒</div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">MoreView Security Engine</h2>
          <p className="text-slate-400 text-xs mt-1.5 mb-8">Enter system credentials to mount administration records loop.</p>
          
          <input
            type="password"
            required
            value={inputKey}
            onChange={(e) => setInputKey(e.target.value)}
            placeholder="Enter Master Key Sequence"
            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 rounded-xl px-4 py-3.5 text-center text-sm text-slate-900 placeholder-slate-400 focus:outline-none transition-colors mb-5 font-medium"
          />
          <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-indigo-600/10 hover:shadow-indigo-600/20">
            Unlock Records Node
          </button>
        </form>
      </div>
    );
  }

  // Authenticated Admin Dashboard Layout - Crisp Widescreen Content Matrix
  return (
    <div className="bg-slate-50 text-slate-800 min-h-screen py-12 font-sans antialiased relative z-10">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-12 lg:px-24">

        {/* HEADER TOOLBAR METRICS CONTROLS */}
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-10 border-b border-slate-200/80 pb-8">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Admin Content Management</h1>
            <p className="text-sm text-slate-400 mt-1">Audit, monitor, and route row entries inside the relational table schema.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsAuthenticated(false)}
              className="text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 px-4 py-3 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              Lock Node
            </button>
            <Link
              to="/admin/create"
              className="text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-5 py-3 rounded-xl shadow-lg shadow-indigo-600/10 hover:shadow-indigo-600/20 transition-all flex items-center gap-1"
            >
              ➕ Create New Post
            </Link>
          </div>
        </div>

        {/* METRIC INDEX COUNTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <div className="text-slate-400 text-[10px] font-mono font-bold uppercase tracking-wider">Total Rows Synchronized</div>
            <div className="text-3xl font-black text-slate-900 mt-1">{blogs.length}</div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <div className="text-slate-400 text-[10px] font-mono font-bold uppercase tracking-wider">Database Access Status</div>
            <div className="text-xs font-bold text-emerald-600 mt-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span> POOL_ACTIVE
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <div className="text-slate-400 text-[10px] font-mono font-bold uppercase tracking-wider">Storage Node Allocation</div>
            <div className="text-xs font-mono text-indigo-600 mt-3 bg-indigo-50 border border-indigo-100/60 px-2.5 py-1 rounded-md w-fit">
              /home/moreview/uploads
            </div>
          </div>
        </div>

        {/* DATA CONTAINER INTERFACE */}
        {loading && <p className="text-sm font-mono text-slate-400 text-center py-20 animate-pulse">Syncing index maps...</p>}
        {error && <p className="text-sm font-mono text-red-600 bg-red-50 border border-red-100 p-4 rounded-xl max-w-md mx-auto text-center">⚠️ {error}</p>}

        {!loading && !error && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xl shadow-slate-200/40">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-400 text-[10px] font-bold font-mono uppercase tracking-wider">
                    <th className="py-4 px-6">Image Pointer</th>
                    <th className="py-4 px-6">Article Headline</th>
                    <th className="py-4 px-6">Category Tag</th>
                    <th className="py-4 px-6">Routing Slug Handle</th>
                    <th className="py-4 px-6 text-right">Actions Panel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
                  {blogs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-16 text-slate-400 text-sm font-medium bg-white">
                        No articles detected in database query blocks.
                      </td>
                    </tr>
                  ) : (
                    blogs.map((blog) => (
                      <tr key={blog.id} className="hover:bg-slate-50/40 transition-colors group">
                        
                        {/* 1. THUMBNAIL MATRIX */}
                        <td className="py-4 px-6">
                          <div className="w-14 h-9 bg-slate-100 border border-slate-200/80 rounded-lg overflow-hidden flex items-center justify-center relative shadow-inner">
                            {blog.top_image ? (
                              <img src={`${blog.top_image}`} alt="" className="w-full h-full object-cover"/>
                            ) : (
                              <span className="text-[8px] text-slate-300 font-mono font-bold">NULL</span>
                            )}
                          </div>
                        </td>

                        {/* 2. TITLE FIELD */}
                        <td className="py-4 px-6 font-bold text-slate-900 max-w-xs truncate">
                          {blog.title}
                        </td>

                        {/* 3. CLASSIFICATION TAG */}
                        <td className="py-4 px-6">
                          <span className={`text-[10px] font-extrabold tracking-wider uppercase px-2.5 py-1 rounded-md border ${getTagColorClass(blog.tag)}`}>
                            {blog.tag || 'Insight'}
                          </span>
                        </td>

                        {/* 4. PERMALINK SLUG */}
                        <td className="py-4 px-6 font-mono text-xs text-slate-400 group-hover:text-indigo-600 transition-colors">
                          /{blog.slug}
                        </td>

                        {/* 5. QUICK-ACTION CONTROLS */}
                        <td className="py-4 px-6 text-right">
                          <div className="flex justify-end items-center gap-2">
                            {/* NEW EDIT ROUTE CONTROLLER */}
                            <Link
                              to={`/admin/edit/${blog.id}`}
                              className="text-xs bg-white hover:bg-amber-50 text-amber-600 border border-slate-200 hover:border-amber-200 px-3 py-2 rounded-xl transition-all font-bold shadow-sm"
                            >
                              ✏️ Edit Post
                            </Link>
                            <Link
                              to={`/blog/${blog.slug}`}
                              className="text-xs bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-xl transition-all font-bold shadow-sm"
                            >
                              👁️ View
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminBlogList;
